import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Loader2,
  Shield,
  Key,
} from "lucide-react";
import { toast } from "react-toastify";
import loginRequests from "../../../api/Customer/LoginRequests";

const ResetPassword = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const inputRefs = useRef([]);

  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const userSlug = location.state?.user_slug;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
      return;
    }
  }, [email, navigate]);

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/\d/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 4);
    if (!/^\d+$/.test(pastedData)) return;
    const newOtp = pastedData.split("").concat(["", "", "", ""]).slice(0, 4);
    setOtp(newOtp);
    const nextIndex = Math.min(pastedData.length, 3);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setFieldError, setStatus }
  ) => {
    try {
      setStatus(null);

      const otpValue = otp.join("");
      if (otpValue.length !== 4) {
        setStatus("Please enter the complete 4-digit OTP");
        return;
      }

      const payload = {
        password: values.password,
        user_slug: userSlug,
        otp: parseInt(otpValue),
      };

      const response = await loginRequests.resetPassword(payload);

      if (response.success) {
        setResetSuccess(true);
        setTimeout(() => {
          navigate("/login", {
            state: {
              message:
                "Password reset successful! Please login with your new password.",
            },
          });
        }, 2000);
      } else {
        const errorData = response.error;

        if (errorData.errors) {
          Object.keys(errorData.errors).forEach((field) => {
            const fieldMap = {
              password: "password",
              otp: "otp",
              user_slug: "user_slug",
            };

            const formField = fieldMap[field] || field;
            if (formField === "otp") {
              setStatus(errorData.errors[field][0]);
            } else {
              setFieldError(formField, errorData.errors[field][0]);
            }
          });
        } else if (errorData.message) {
          if (errorData.message.toLowerCase().includes("otp")) {
            setStatus(errorData.message);
            setOtp(["", "", "", ""]);
            inputRefs.current[0]?.focus();
          } else if (errorData.message.toLowerCase().includes("password")) {
            setFieldError("password", errorData.message);
          } else {
            setStatus(errorData.message);
            toast.error(errorData.message);
          }
        } else {
          setStatus("Password reset failed. Please try again.");
        }
      }
    } catch (error) {
      console.error("Password reset error:", error);
      setStatus("An unexpected error occurred. Please try again.");
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    return validations;
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left section with info - hidden on mobile, fixed position on desktop */}
      <div
        className={`hidden md:flex w-2/5 bg-gradient-to-br from-purple-600 to-pink-600 text-white p-16 flex-col justify-between fixed h-screen overflow-hidden transition-opacity duration-500 ease-in-out ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        <div>
          <h1 className="text-4xl font-bold mb-6">Create New Password</h1>
          <p className="text-lg opacity-90 mb-8">
            You're almost done! Enter the OTP from your email and create a
            secure new password.
          </p>

          <div className="space-y-6 mt-10">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Key className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-lg">OTP Verification</h3>
                <p className="opacity-80 text-sm">
                  Enter the 4-digit code sent to your email
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Strong Password</h3>
                <p className="opacity-80 text-sm">
                  Choose a password that meets all security requirements
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="p-2 bg-white/20 rounded-full">
                <CheckCircle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Account Recovery</h3>
                <p className="opacity-80 text-sm">
                  Regain full access to your TXTConnect account
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12">
          <p className="text-sm opacity-80">
            Remember your password?{" "}
            <Link
              to="/login"
              className="font-medium underline hover:text-white transition-all duration-300"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right section with form - full width on mobile, adjusted for fixed sidebar on desktop */}
      <div
        className={`w-full md:w-3/5 p-8 md:p-16 md:ml-[40%] bg-white transition-transform duration-500 ease-in-out ${
          isVisible ? "translate-x-0" : "translate-x-8"
        }`}
      >
        <div className="w-full max-w-md mx-auto">
          {/* Mobile-only header */}
          <div className="md:hidden text-center mb-8">
            <p className="text-sm text-gray-600">
              Remember your password?{" "}
              <Link
                to="/login"
                className="font-medium text-purple-600 hover:text-purple-500 transition-all duration-300"
              >
                Sign in here
              </Link>
            </p>
          </div>

          {!resetSuccess ? (
            <>
              <div
                className={`text-center md:text-left mb-10 transition-transform duration-500 ease-in-out ${
                  isVisible ? "translate-y-0" : "translate-y-4"
                }`}
              >
                <h2 className="text-3xl font-bold text-gray-800">
                  Reset Your Password
                </h2>
                <p className="text-gray-600 mt-2">
                  Enter the OTP sent to{" "}
                  <span className="font-medium text-purple-600">{email}</span>{" "}
                  and create your new password
                </p>
              </div>

              <Formik
                initialValues={{ password: "", confirmPassword: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, status, values }) => {
                  const passwordValidation = validatePassword(values.password);
                  const passwordsMatch =
                    values.password === values.confirmPassword &&
                    values.confirmPassword !== "";

                  return (
                    <Form
                      className={`space-y-6 transition-opacity duration-700 ease-in-out ${
                        isVisible ? "opacity-100" : "opacity-0"
                      }`}
                    >
                      {/* General error message */}
                      {status && (
                        <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <p className="text-sm text-red-700">{status}</p>
                        </div>
                      )}

                      {/* OTP Input */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Verification Code
                        </label>
                        <div className="flex space-x-3 justify-center">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              ref={(el) => (inputRefs.current[index] = el)}
                              type="text"
                              maxLength="1"
                              value={digit}
                              onChange={(e) =>
                                handleOtpChange(index, e.target.value)
                              }
                              onKeyDown={(e) => handleKeyDown(index, e)}
                              onPaste={handlePaste}
                              disabled={isSubmitting || resetSuccess}
                              className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:border-purple-500 focus:ring-purple-500 outline-none transition-all duration-300 disabled:bg-gray-50 disabled:text-gray-500"
                            />
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">
                          Enter the 4-digit code sent to your email
                        </p>
                      </div>

                      {/* Password Field */}
                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          New Password
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Lock className="h-5 w-5 text-gray-400" />
                          </div>
                          <Field
                            type={showPassword ? "text" : "password"}
                            name="password"
                            id="password"
                            disabled={isSubmitting || resetSuccess}
                            className="pl-10 block w-full rounded-lg border border-gray-300 py-3 shadow-sm focus:border-purple-500 focus:ring-purple-500 outline-none transition-all duration-300 disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={isSubmitting || resetSuccess}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center transition-all duration-300 disabled:opacity-50"
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                              <Eye className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />

                        {/* Password Validation */}
                        {values.password && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <h4 className="text-xs font-medium text-gray-700 mb-2">
                              Password Requirements:
                            </h4>
                            <div className="grid grid-cols-1 gap-1">
                              {[
                                {
                                  key: "length",
                                  label: "8+ characters",
                                  valid: passwordValidation.length,
                                },
                                {
                                  key: "uppercase",
                                  label: "Uppercase letter",
                                  valid: passwordValidation.uppercase,
                                },
                                {
                                  key: "lowercase",
                                  label: "Lowercase letter",
                                  valid: passwordValidation.lowercase,
                                },
                                {
                                  key: "number",
                                  label: "Number",
                                  valid: passwordValidation.number,
                                },
                                {
                                  key: "special",
                                  label: "Special character",
                                  valid: passwordValidation.special,
                                },
                              ].map(({ key, label, valid }) => (
                                <div
                                  key={key}
                                  className="flex items-center space-x-2"
                                >
                                  <div
                                    className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                      valid ? "bg-red-100" : "bg-red-100"
                                    }`}
                                  >
                                    <span className="text-xs">
                                      {valid ? "✓" : "✗"}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-xs ${
                                      valid ? "text-red-700" : "text-red-700"
                                    }`}
                                  >
                                    {label}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Confirm Password Field */}
                      <div>
                        <label
                          htmlFor="confirmPassword"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Lock className="h-5 w-5 text-gray-400" />
                          </div>
                          <Field
                            type={showConfirmPassword ? "text" : "password"}
                            name="confirmPassword"
                            id="confirmPassword"
                            disabled={isSubmitting || resetSuccess}
                            className="pl-10 block w-full rounded-lg border border-gray-300 py-3 shadow-sm focus:border-purple-500 focus:ring-purple-500 outline-none transition-all duration-300 disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="••••••••"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            disabled={isSubmitting || resetSuccess}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center transition-all duration-300 disabled:opacity-50"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-5 w-5 text-gray-400" />
                            ) : (
                              <Eye className="h-5 w-5 text-gray-400" />
                            )}
                          </button>
                        </div>
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="mt-1 text-sm text-red-600"
                        />

                        {/* Password Match Validation */}
                        {values.confirmPassword && (
                          <div className="mt-2 flex items-center space-x-2">
                            <div
                              className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                passwordsMatch ? "bg-red-100" : "bg-red-100"
                              }`}
                            >
                              <span className="text-xs">
                                {passwordsMatch ? "✓" : "✗"}
                              </span>
                            </div>
                            <span
                              className={`text-xs ${
                                passwordsMatch ? "text-red-700" : "text-red-700"
                              }`}
                            >
                              {passwordsMatch
                                ? "Passwords match"
                                : "Passwords do not match"}
                            </span>
                          </div>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={
                          isSubmitting ||
                          resetSuccess ||
                          otp.join("").length !== 4 ||
                          !Object.values(passwordValidation).every(Boolean) ||
                          !passwordsMatch
                        }
                        className="group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-lg text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 font-medium shadow-md transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Resetting password...
                          </>
                        ) : (
                          <>
                            Reset Password
                            <CheckCircle className="ml-2 w-5 h-5 group-hover:scale-110 transition-transform duration-150" />
                          </>
                        )}
                      </button>

                      <div className="mt-6 text-center">
                        <Link
                          to="/forgot-password"
                          className="flex items-center justify-center space-x-1 text-sm text-gray-600 hover:text-gray-800 transition-all duration-300"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          <span>Back to forgot password</span>
                        </Link>
                      </div>
                    </Form>
                  );
                }}
              </Formik>
            </>
          ) : (
            // Success State
            <div
              className={`text-center transition-all duration-500 ease-in-out ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div className="mx-auto w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">
                Password Reset Successful!
              </h2>
              <p className="text-gray-600 mb-6">
                Your password has been successfully reset. You will be
                redirected to the login page shortly.
              </p>
              <div className="flex items-center justify-center space-x-2 text-purple-600">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Redirecting to login...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
